import { scrollToTop } from 'helpers/domHelper';
import { withConfigLoader, withPublicConfigLoader } from 'hoc/config.hoc';
import { withCreditInfo } from 'hoc/creditInfo.hoc';
import { withEventHandler } from 'hoc/eventHandler.hoc';
import { withFeaturedAd } from 'hoc/featuredAd.hoc';
import { withSesameCash } from 'hoc/sesameCash.hoc';
import { withUserInfo } from 'hoc/user.hoc';
import { withUserSubscriptionInfo } from 'hoc/userSubscriptionInfo.hoc';
import { usePageLoadingAction, usePageStateSetter } from 'hook/page.hook';
import React, { useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isOcfPage } from 'helpers/ocf.helpers';
import {
  foundUserOnInvalidRoute,
  publicLogin,
  loginSuccess,
} from 'actions/login.actions';
import { selectStandardSecureData } from 'selectors/secure_data.selector';
import * as pageNameTypes from 'types/mrph.pageName.constants';
import { useEventHandler } from 'hook/event.hook';
import { OCF_LOGOUT } from 'types/app.constants';
import useTrackingMetadata from 'externals/_tracking/useTrackingMetadata';
import { getSessionRole, OCF_ROLE, USER_ROLE } from 'helpers/session';
import { useLogger } from 'hook/error.hook';
import { isOcfSessionValid } from 'helpers/sessionLocalStorageReader.helper';
import { isLoginFromLogout } from 'helpers/login.helpers';
import { selectPageState } from '../selectors/page.selector';
import {
  selectCreditInfo,
  selectUserInfo,
  selectOcfInfo,
} from '../selectors/api.selector';
import { setUserLogoutTime } from '../helpers/localstorage';
import { getMoment } from 'helpers/dateHelper';
import { setHideHeader } from 'helpers/sessionstorage';
import { getCookieValue } from 'helpers/cookie.helper';
import { identifyUser } from '../externals/_tracking/impact/impact.track';
import { OVERVIEW_URL } from 'types/mrph.routes.constants';
import { isPendingPaymentOnSignUp } from 'helpers/userHelper';

export const withSecureServicesData = Cmp => {
  const WithSecureServicesDataCmp = props => {
    const state = useSelector(state => ({
      ...selectStandardSecureData(state),
    }));

    const eventHandler = useEventHandler();

    return <Cmp {...props} {...state} eventHandler={eventHandler} />;
  };

  return WithSecureServicesDataCmp;
};

export const withUserAndCreditInfoServicesMobile = (Cmp, opts) => {
  const {
    doNotForward,
    pageNameType,
    isOCFAllowed,
    hideHeader,
    avoidLoginSucess,
  } = opts ?? {};
  const mrphPageName = pageNameType ? pageNameTypes[pageNameType] : null;
  const finalSelector = opts?.selector ?? (_ => {});
  const isUserSessionActive = getSessionRole() === USER_ROLE;
  const WithUserAndCreditInfoHoc = props => {
    setHideHeader(hideHeader);

    const dispatch = useDispatch();
    const eventHandler = useEventHandler();
    const setState = usePageStateSetter({ pageNameType });

    const state = useSelector(state => {
      const pageName = state?.page?.pageName;
      return {
        ...finalSelector(state),
        pageNameMeta: state.tracking.pageNameMeta,
        pageName,
        ...selectPageState(state),
        ...selectUserInfo(state),
        ...selectOcfInfo(state),
        ...selectCreditInfo(state),
        state: state?.page[pageName],
      };
    });
    const { loggedIn, loggingIn, ocfLoggedIn, ocfInfo, pageNameMeta } = state;

    const trackingManager = useTrackingMetadata();
    let trackingLoadingFlag =
      !pageNameMeta || (pageNameType ? pageNameMeta !== mrphPageName : false);
    let globalLoadingFlag =
      props.creditInfoLoading ||
      props.userInfoLoading ||
      trackingLoadingFlag ||
      props.configLoading;

    const isForcingLogin = loggedIn === false;

    const isOcfRole = ocfLoggedIn === true;

    if (isOcfRole) {
      trackingLoadingFlag =
        !pageNameMeta || (pageNameType ? pageNameMeta !== mrphPageName : false);
      globalLoadingFlag = trackingLoadingFlag || props.configLoading;
    }

    useEffect(() => {
      const sessionError = !(
        USER_ROLE === getSessionRole() ||
        (isOCFAllowed && OCF_ROLE === getSessionRole())
      );

      const isOcfSessionRedirect =
        isOcfSessionValid() && !loggedIn && !loggingIn && !isOCFAllowed;
      const ocfError =
        !isOcfPage(props.location) && (ocfLoggedIn || isOcfSessionRedirect);
      if (sessionError || ocfError) {
        if (!isForcingLogin) {
          // IF the user has a session error and is logged in
          // THEN logout to clear session
          props.history.push('/logout');
        } else {
          // IF the user has a session error and is not logged in
          // THEN setup redirect and show user login
          if (!doNotForward && !global.isUnitTestSuite) {
            // skip forwarding on sub pages such as container manager
            const ocfEmail = ocfInfo?.userInfo?.email || ocfInfo?.email || null;
            if (ocfError) {
              dispatch({
                type: OCF_LOGOUT,
              });
            }
            dispatch(
              foundUserOnInvalidRoute(
                props.location,
                props.history,
                isOcfRole,
                ocfEmail,
              ),
            );
          }
        }
      }
      //eslint-disable-next-line
    }, [
      props.userInfoError,
      props.creditInfoError,
      props.configError,
      props.history,
    ]);

    useEffect(() => {
      if (mrphPageName && mrphPageName !== pageNameMeta) {
        trackingManager.buildPageMeta(pageNameType);
      }

      //eslint-disable-next-line
    }, []);

    useEffect(() => {
      if (!isUserSessionActive && !avoidLoginSucess) {
        setUserLogoutTime(getMoment().add(5, 'minutes').format());
        dispatch(loginSuccess(null, null, getCookieValue('sessionIdentifier')));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserSessionActive]);

    useEffect(() => {
      if (props.userInfo && isPendingPaymentOnSignUp(props.userInfo)) {
        props.history.push('/d2p/checkout');
      }
      //eslint-disable-next-line
    }, [props.userInfo]);

    return (
      <Cmp
        {...props}
        {...state}
        globalLoadingFlag={globalLoadingFlag}
        eventHandler={eventHandler}
        setPageState={setState}
        dispatch={dispatch}
      />
    );
  };

  return withRouter(
    withConfigLoader(
      withUserInfo(withCreditInfo(withEventHandler(WithUserAndCreditInfoHoc))),
    ),
  );
};

/**
 *
 *
 *
 * @param Cmp
 * @param mixpanelPageName
 * @returns {function(*): *}
 */
export const withSecureServices = (Cmp, opts) => {
  const { doNotForward, pageNameType, isOCFAllowed } = opts ?? {};

  const mrphPageName = pageNameType ? pageNameTypes[pageNameType] : null;

  const finalSelector = opts?.selector ?? (_ => {});
  const WithSecureServicesHoC = props => {
    const state = useSelector(state => ({
      ...selectStandardSecureData(state),
      ...finalSelector(state),

      signup: state.signup,
    }));
    const { loggedIn, loggingIn, ocfLoggedIn, ocfInfo, pageNameMeta, signup } =
      state;

    const eventHandler = useEventHandler();
    const dispatch = useDispatch();
    // not in use....yet
    const loadingActions = opts?.loadingActions ?? [];
    const allLoaderConfigs = [
      // buildUserInfoLoader(),
      // buildCreditInfoLoader(),
      // buildSesameCashLoader(),
      // //buildSesameCashLoader(),
      // buildFeaturedAdsLoader(),
      // buildConfigLoader(),
      loadingActions,
    ];
    usePageLoadingAction({
      loadingActions: allLoaderConfigs,
    });

    useEffect(() => {
      if (opts?.scrollToTop) {
        scrollToTop();
      }
    }, []);
    const setState = usePageStateSetter({ pageNameType });
    const trackingManager = useTrackingMetadata();
    let trackingLoadingFlag =
      !pageNameMeta || (pageNameType ? pageNameMeta !== mrphPageName : false);
    let globalLoadingFlag =
      props.creditInfoLoading ||
      props.userInfoLoading ||
      trackingLoadingFlag ||
      props.configLoading ||
      props.featuredAdLoading ||
      props.cashLoading;

    const isForcingLogin = loggedIn === false;

    const isOcfRole = ocfLoggedIn === true;

    if (isOcfRole) {
      trackingLoadingFlag =
        !pageNameMeta || (pageNameType ? pageNameMeta !== mrphPageName : false);
      globalLoadingFlag = trackingLoadingFlag || props.configLoading;
    }

    useEffect(() => {
      const sessionError = !(
        USER_ROLE === getSessionRole() ||
        (isOCFAllowed && OCF_ROLE === getSessionRole())
      );

      const isOcfSessionRedirect =
        isOcfSessionValid() && !loggedIn && !loggingIn && !isOCFAllowed;
      const ocfError =
        !isOcfPage(props.location) && (ocfLoggedIn || isOcfSessionRedirect);
      if (sessionError || ocfError) {
        if (!isForcingLogin) {
          // IF the user has a session error and is logged in
          // THEN logout to clear session
          props.history.push('/logout');
        } else {
          // IF the user has a session error and is not logged in
          // THEN setup redirect and show user login
          if (!doNotForward && !global.isUnitTestSuite) {
            // skip forwarding on sub pages such as container manager
            const ocfEmail = ocfInfo?.userInfo?.email || ocfInfo?.email || null;
            if (ocfError) {
              dispatch({
                type: OCF_LOGOUT,
              });
            }
            dispatch(
              foundUserOnInvalidRoute(
                props.location,
                props.history,
                isOcfRole,
                ocfEmail,
              ),
            );
          }
        }
      }
      //eslint-disable-next-line
    }, [
      props.userInfoError,
      props.creditInfoError,
      props.configError,
      props.history,
    ]);

    useEffect(() => {
      if (mrphPageName && mrphPageName !== pageNameMeta) {
        trackingManager.buildPageMeta(pageNameType);
        identifyUser();
      }
      //eslint-disable-next-line
    }, []);

    useEffect(() => {
      if (props.userInfo && !props.isInvisibleUser) {
        if (
          props.userInfo.status === 'pending' &&
          !signup.tryLoginWelcomeBack
        ) {
          props.history.push('/signup2');
        } else if (isPendingPaymentOnSignUp(props.userInfo)) {
          props.history.push('/d2p/checkout');
        }
      }
      //eslint-disable-next-line
    }, [props.userInfo, props.isInvisibleUser]);

    return (
      <Cmp
        {...props}
        {...state}
        globalLoadingFlag={globalLoadingFlag}
        isOcfRole={isOcfRole}
        setPageState={setState}
        eventHandler={eventHandler}
        dispatch={dispatch}
      />
    );
  };

  return withRouter(
    withConfigLoader(
      withFeaturedAd(
        withUserInfo(
          withUserSubscriptionInfo(
            withCreditInfo(
              withSesameCash(withEventHandler(WithSecureServicesHoC)),
            ),
          ),
        ),
      ),
    ),
  );
};

export const withPublicServices = (Cmp, opts) => {
  const finalOpts = opts ? opts : {};
  const { loggedOutOnly, isLoginPage, pageNameType, ignorePublicLogin } =
    finalOpts;

  const mrphPageName = pageNameType ? pageNameTypes[pageNameType] : null;

  const WithPublicService = props => {
    const dispatch = useDispatch();
    const eventHandler = useEventHandler();
    const logger = useLogger({ name: 'page.hoc' });
    const { loggedIn, loggingIn, publicLoggingIn } = useSelector(state => ({
      loggedIn: state.app.loggedIn,
      loggingIn: state.app.loggingIn,
      publicLoggingIn: state.app.publicLoggingIn,
    }));
    const localStorageRole = getSessionRole();
    const isRoleUserLoggedIn =
      loggedIn === true || loggingIn === true || localStorageRole === USER_ROLE;
    const isLoggedIn = isRoleUserLoggedIn || localStorageRole !== null;

    const trackingManager = useTrackingMetadata();
    const { buildPageMeta, pageNameMeta } = trackingManager;

    if (mrphPageName && mrphPageName !== pageNameMeta) {
      buildPageMeta(pageNameType);
    }

    const shouldForwardToDashboard =
      loggedOutOnly && isRoleUserLoggedIn && !loggingIn;
    if (shouldForwardToDashboard) {
      props.history.push(OVERVIEW_URL);
    }

    const isRegularLoginPage = isLoginPage && !isLoginFromLogout();

    const shouldStartPublicSession =
      !isLoggedIn && !isLoginPage && !ignorePublicLogin;

    let globalLoadingFlag = shouldStartPublicSession && !publicLoggingIn;

    //start a public session
    useEffect(() => {
      if (shouldStartPublicSession) {
        dispatch(publicLogin()).catch(err => {
          logger.errorException('Unable to login with api key', err);
        });
      }
      //eslint-disable-next-line
    }, [isLoggedIn, isRegularLoginPage]);

    return (
      <Cmp
        {...props}
        eventHandler={eventHandler}
        globalLoadingFlag={globalLoadingFlag}
      />
    );
  };

  return withRouter(withPublicConfigLoader(WithPublicService));
};
